import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api, formatApiErrorDetail, trackStripeCta } from "@/lib/api";
import { openBillingPortal, startPlanCheckout } from "@/lib/billing";
import { useAuth } from "@/context/AuthContext";
import { Check, Crown, ArrowRight, Sparkle } from "@phosphor-icons/react";

const BLURB = {
  starter: "For your first business — books, invoices, taxes, and Rowan in your corner.",
  professional: "For growing businesses — advanced reporting, client portal, and AI insights.",
  business: "For multiple businesses or a full team — unlimited entities, team seats, full platform.",
};

const FEATURES = {
  starter: [
    "1 business entity",
    "AI bookkeeping (Rowan)",
    "Invoicing",
    "Receipt scanning",
    "Basic P&L and balance sheet",
    "Bank connection",
    "Quarterly tax estimates",
  ],
  professional: [
    "Everything in Starter",
    "Up to 3 business entities",
    "Advanced reporting & dashboards",
    "Client portal",
    "AI insights & anomaly detection",
    "Payroll tracking",
    "Inventory basics",
  ],
  business: [
    "Everything in Professional",
    "Unlimited business entities",
    "Unlimited team seats",
    "Dedicated account manager",
    "Custom Chart of Accounts",
    "API access",
    "Priority support",
    "White-label reports",
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data));
  }, []);

  const [portalLoading, setPortalLoading] = useState(false);
  const [founding, setFounding] = useState(null);

  useEffect(() => {
    api.get("/billing/founding-status").then((r) => setFounding(r.data)).catch(() => {});
  }, []);
  const [checkoutTier, setCheckoutTier] = useState("");
  const startCheckout = async (tier, label) => {
    setCheckoutTier(tier);
    trackStripeCta(label, "pricing_page", `Pricing ${label} CTA`);
    try {
      await startPlanCheckout(tier);
    } catch (err) {
      toast.error(err.message);
      setCheckoutTier("");
    }
  };
  const openPortal = async () => {
    setPortalLoading(true);
    try {
      await openBillingPortal({ returnPath: "/pricing" });
    } catch (err) {
      toast.error(err.message || formatApiErrorDetail(err.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Pricing</span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3" style={{ color: "var(--bk-text)" }}>
            Simple, honest pricing for business owners.
          </h1>
          <p className="text-base mt-4" style={{ color: "var(--bk-text-2)" }}>
            Every plan starts with a <span className="font-semibold" style={{ color: "var(--bk-text)" }}>14-day free trial — no card required</span>. Upgrade as your business grows, and the <span className="font-semibold" style={{ color: "var(--bk-text)" }}>first 10 founding members</span> lock in
            <span className="font-semibold" style={{ color: "var(--bk-accent)" }}> 40% off for life</span> — the FOUNDING40 discount is applied automatically at checkout.
          </p>
        </div>

        {/* Founding member spotlight */}
        <div data-testid="founding-banner" className="max-w-3xl mx-auto mb-12 rounded-2xl p-5 sm:p-6 text-center relative overflow-hidden" style={{ background: "var(--bk-hero-bg)" }}>
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-2xl opacity-40" style={{ background: "var(--bk-gradient-primary)" }} />
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "var(--bk-gradient-energy)", color: "#fff" }}>
              <Sparkle size={13} weight="fill" /> Founding Member offer
            </span>
            <p className="font-display text-xl sm:text-2xl font-black text-white mt-3">
              First 10 members save <span style={{ color: "#F0B429" }}>40% for life</span>
            </p>
            {founding && founding.left !== null && (
              <div data-testid="founding-counter" className="mt-3 inline-flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm font-bold px-3.5 py-1.5 rounded-full" style={{ background: "rgba(240,180,41,0.15)", color: "#FFD34E", border: "1px solid rgba(240,180,41,0.4)" }}>
                  {founding.left > 0 ? `${founding.left} of ${founding.total} spots left` : "All founding spots claimed"}
                </span>
                <span className="flex gap-1" aria-hidden="true">
                  {Array.from({ length: founding.total }).map((_, i) => (
                    <span key={i} className="w-2 h-2 rounded-full" style={{ background: i < founding.left ? "#F0B429" : "rgba(255,255,255,0.25)" }} />
                  ))}
                </span>
              </div>
            )}
            <p className="text-sm mt-1.5" style={{ color: "rgba(255,255,255,0.8)" }}>
              Code <span className="font-mono font-bold" style={{ color: "#FFD34E" }}>FOUNDING40</span> is applied automatically at checkout — the discount sticks forever, on any plan.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p, i) => {
            const current = (user?.plan || "free") === p.tier;
            const highlight = p.tier === "professional";
            const planLabel = p.name;
            return (
              <div
                key={p.tier}
                data-testid={`plan-${p.tier}`}
                className="bk-card p-7 flex flex-col bk-fade-up"
                style={{ borderColor: highlight ? "var(--bk-accent)" : "var(--bk-border)", borderWidth: highlight ? 2 : 1, animationDelay: `${0.06 * i}s` }}
              >
                {highlight && (
                  <span className="self-start text-xs font-semibold px-2.5 py-1 rounded-full mb-3 inline-flex items-center gap-1" style={{ background: "var(--bk-accent)", color: "#fff" }}>
                    <Crown size={13} weight="fill" /> Most Popular
                  </span>
                )}
                <h3 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>{p.name}</h3>
                <div className="mt-3 mb-1 flex items-end gap-1">
                  <span className="font-display text-4xl font-bold" style={{ color: "var(--bk-text)" }}>${p.price}</span>
                  <span className="text-sm mb-1.5" style={{ color: "var(--bk-text-2)" }}>/mo</span>
                </div>
                <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" data-testid={`founding-price-${p.tier}`} style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>
                  <Sparkle size={12} weight="fill" /> Founding: ${(p.price * 0.6).toFixed(2)}/mo for life
                </div>
                <p className="text-sm mb-5" style={{ color: "var(--bk-text-2)" }}>{BLURB[p.tier]}</p>

                <ul className="space-y-2 mb-6">
                  {(FEATURES[p.tier] || []).map((f) => (
                    <li key={f} className="flex gap-2 text-sm" style={{ color: "var(--bk-text-2)" }}>
                      <Check size={17} weight="bold" color="var(--bk-success)" className="shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  {current ? (
                    <div className="w-full py-2.5 rounded-md text-sm font-semibold text-center border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid={`current-${p.tier}`}>
                      Current plan
                    </div>
                  ) : p.tier === "free" ? (
                    <div className="w-full py-2.5 rounded-md text-sm font-semibold text-center" style={{ color: "var(--bk-disabled)" }}>—</div>
                  ) : user ? (
                    <button
                      type="button"
                      onClick={() => startCheckout(p.tier, planLabel)}
                      disabled={!!checkoutTier}
                      data-testid={`upgrade-${p.tier}`}
                      className="bk-btn-primary w-full py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {checkoutTier === p.tier ? "Opening checkout…" : "Upgrade"} {checkoutTier !== p.tier && <ArrowRight size={16} weight="bold" />}
                    </button>
                  ) : (
                    <Link
                      to="/register"
                      onClick={() => trackStripeCta(planLabel, "pricing_page", `Pricing ${planLabel} CTA`)}
                      data-testid={`start-${p.tier}`}
                      className="bk-btn-primary w-full py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2"
                    >
                      Start free trial <ArrowRight size={16} weight="bold" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {user?.account_state === "active" && (
          <div className="text-center mt-8">
            <button
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="manage-billing-btn"
              className="text-sm font-semibold underline underline-offset-2 disabled:opacity-60"
              style={{ color: "var(--bk-text-2)" }}
            >
              {portalLoading ? "Opening…" : "Manage plan & billing · Cancel"}
            </button>
          </div>
        )}

        <div className="max-w-2xl mx-auto mt-10 text-center" data-testid="billing-policy">
          <p className="text-sm leading-relaxed" style={{ color: "var(--bk-text-2)" }}>
            <span className="font-semibold" style={{ color: "var(--bk-text)" }}>Cancel anytime.</span> You'll keep full access
            through the end of your current billing month and won't be charged again. Because every plan includes a 14-day free
            trial, all sales are final and we don't offer refunds.
          </p>
        </div>
      </main>
    </div>
  );
}
